import { z } from 'zod';

export const SCHEMA_VERSION = '1.0.0' as const;

export const VERIFICATION_CAPABILITIES = Object.freeze({
  'image/jpeg': true,
  'image/png': true,
  c2pa: true,
  metadata: true,
  openai_provenance: false,
  synthid: false,
  video: false,
  audio: false,
} as const);

export const verificationCapabilitiesSchema = z.object({
  'image/jpeg': z.boolean(),
  'image/png': z.boolean(),
  c2pa: z.boolean(),
  metadata: z.boolean(),
  openai_provenance: z.boolean(),
  synthid: z.boolean(),
  video: z.boolean(),
  audio: z.boolean(),
});

export const overallStatusSchema = z.enum([
  'VERIFIED_PROVENANCE',
  'PROVENANCE_PRESENT',
  'INVALID_OR_TAMPERED',
  'NO_SUPPORTED_PROVENANCE',
  'INCONCLUSIVE',
  'UNSUPPORTED',
]);

export const trustStatusSchema = z.enum([
  'VALID_SIGNATURE',
  'INVALID_SIGNATURE',
  'TRUSTED_SIGNER',
  'KNOWN_SIGNER',
  'UNKNOWN_SIGNER',
  'EXPIRED_CREDENTIAL',
  'REVOKED',
  'UNTRUSTED_CHAIN',
  'TRUSTED_TIMESTAMP',
  'UNKNOWN_TIMESTAMP',
  'UNTRUSTED_TIMESTAMP',
  'TRUST_STATUS_UNKNOWN',
]);

export const validitySchema = z.enum(['VALID', 'INVALID', 'UNKNOWN', 'NOT_APPLICABLE']);
export const EVIDENCE_METHODS = [
  'CRYPTOGRAPHIC_PROVENANCE',
  'EMBEDDED_METADATA',
  'TRUST_REGISTRY',
  'TIMESTAMP_TRUST',
  'INVISIBLE_WATERMARK',
  'PROBABILISTIC_DETECTION',
  'DEVICE_ATTESTATION',
  'EXTERNAL_REGISTRY',
  'DECLARATIVE_ASSERTION',
] as const;
export const EVIDENCE_STRENGTHS = [
  'DIRECT_CRYPTOGRAPHIC',
  'DIRECT_REGISTRY',
  'DIRECT_MACHINE_SIGNAL',
  'PROVIDER_DETECTION',
  'DECLARED',
  'DERIVED',
  'UNKNOWN',
] as const;
export const evidenceMethodSchema = z.enum(EVIDENCE_METHODS);
export const evidenceStrengthSchema = z.enum(EVIDENCE_STRENGTHS);
export const evidenceIdSchema = z.string().min(3).max(160);
export const isoTimestampSchema = z.iso.datetime({ offset: true });

export const evidenceSourceSchema = z.object({
  adapter: z.string().min(1),
  adapter_version: z.string().min(1).optional(),
  provider: z.string().min(1),
  native_id: z.string().optional(),
});

export const evidenceBaseSchema = z.object({
  id: evidenceIdSchema,
  source: evidenceSourceSchema,
  evidence_refs: z.array(evidenceIdSchema).default([]),
});

export const artifactSchema = z.object({
  id: evidenceIdSchema,
  filename: z.string().min(1),
  media_type: z.string().min(1),
  format: z.string().min(1),
  byte_size: z.number().int().nonnegative(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
  magic_bytes_valid: z.boolean(),
});

export const signalSchema = evidenceBaseSchema.extend({
  kind: z.string().min(1),
  state: z.enum(['PRESENT', 'ABSENT', 'VALID', 'INVALID', 'UNKNOWN']),
  label: z.string().min(1),
  evidence_method: evidenceMethodSchema.optional(),
  evidence_strength: evidenceStrengthSchema.optional(),
  details: z.record(z.string(), z.unknown()).default({}),
});

export const credentialSchema = evidenceBaseSchema.extend({
  kind: z.string().min(1),
  manifest_present: z.boolean(),
  cryptographic_validity: validitySchema,
  asset_binding_validity: validitySchema,
  signer_actor_id: evidenceIdSchema.optional(),
  issued_at: isoTimestampSchema.optional(),
  expires_at: isoTimestampSchema.optional(),
});

export const claimSchema = evidenceBaseSchema.extend({
  credential_id: evidenceIdSchema.optional(),
  label: z.string().min(1),
  value: z.unknown(),
});

export const assertionSchema = evidenceBaseSchema.extend({
  claim_id: evidenceIdSchema.optional(),
  label: z.string().min(1),
  value: z.unknown(),
});

export const actionSchema = evidenceBaseSchema.extend({
  action_type: z.string().min(1),
  occurred_at: isoTimestampSchema.optional(),
  source_type: z.string().optional(),
  actor_id: evidenceIdSchema.optional(),
  tool_id: evidenceIdSchema.optional(),
  parent_artifact_id: evidenceIdSchema.optional(),
});

export const actorSchema = evidenceBaseSchema.extend({
  name: z.string().min(1),
  identity: z.string().optional(),
  issuer: z.string().optional(),
  signature_algorithm: z.string().optional(),
  identity_status: z.enum(['KNOWN', 'UNKNOWN', 'NOT_PROVIDED']),
});

export const toolSchema = evidenceBaseSchema.extend({
  name: z.string().min(1),
  version: z.string().optional(),
});

export const diagnosticSchema = z.object({
  id: evidenceIdSchema,
  code: z.string().min(1),
  message_key: z.string().min(1),
  evidence_refs: z.array(evidenceIdSchema).default([]),
  source: evidenceSourceSchema.optional(),
});

export const conclusionSchema = z.object({
  id: evidenceIdSchema,
  title_key: z.string().min(1),
  explanation_key: z.string().min(1),
  trace_refs: z.array(evidenceIdSchema).min(1),
});

export const trustResolutionSchema = z.enum(['TRUSTED', 'NOT_TRUSTED', 'UNKNOWN', 'NOT_APPLICABLE']);
export const trustSnapshotFreshnessSchema = z.enum(['CURRENT', 'STALE']);
export const trustSnapshotSchema = z.object({
  source: z.url(),
  policy_id: z.enum(['C2PA_OFFICIAL_TL', 'C2PA_OFFICIAL_TSA_TL', 'C2PA_LEGACY_ITL']),
  policy_version: z.string().min(1),
  retrieved_at: isoTimestampSchema,
  content_digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  anchor_count: z.number().int().nonnegative().max(2_000),
  anchor_fingerprints: z.array(z.string().regex(/^sha256:[a-f0-9]{64}$/)).min(1).max(2_000).optional(),
  status: trustSnapshotFreshnessSchema,
  list_issue_at: isoTimestampSchema.optional(),
  next_update_at: isoTimestampSchema.optional(),
});

export const trustAssessmentSchema = z.object({
  policy_name: z.string().min(1),
  policy_version: z.string().min(1),
  evaluated_at: isoTimestampSchema,
  statuses: z.array(trustStatusSchema).min(1),
  signer_identity: z.string().optional(),
  certificate_chain_validity: validitySchema,
  trust_list_membership: validitySchema,
  policy_id: z.enum(['C2PA_OFFICIAL_TL', 'C2PA_OFFICIAL_TSA_TL', 'C2PA_LEGACY_ITL']).optional(),
  signer_trust: trustResolutionSchema.optional(),
  timestamp_trust: trustResolutionSchema.optional(),
  snapshots: z.array(trustSnapshotSchema).max(4).optional(),
});

export const integrityAssessmentSchema = z.object({
  manifest_presence: z.enum(['PRESENT', 'ABSENT', 'UNKNOWN']),
  cryptographic_validity: validitySchema,
  asset_binding_validity: validitySchema,
});

export const evidenceEdgeSchema = z.object({
  id: evidenceIdSchema,
  source: evidenceSourceSchema.optional(),
  from: evidenceIdSchema,
  to: evidenceIdSchema,
  relation: z.enum([
    'DESCRIBES',
    'CONTAINS',
    'SUPPORTS',
    'CONTRADICTS',
    'SIGNED_BY',
    'CREATED_WITH',
    'PERFORMED_BY',
    'DERIVED_FROM',
  ]),
  evidence_refs: z.array(evidenceIdSchema).default([]),
});

export const verificationResultSchema = z
  .object({
    verification_id: evidenceIdSchema,
    schema_version: z.literal(SCHEMA_VERSION),
    artifact: artifactSchema,
    overall_status: overallStatusSchema,
    signals: z.array(signalSchema),
    credentials: z.array(credentialSchema),
    claims: z.array(claimSchema),
    assertions: z.array(assertionSchema),
    actions: z.array(actionSchema),
    actors: z.array(actorSchema),
    tools: z.array(toolSchema),
    evidence_graph: z.object({ edges: z.array(evidenceEdgeSchema) }),
    trust: trustAssessmentSchema,
    integrity: integrityAssessmentSchema,
    conclusions: z.array(conclusionSchema).min(1),
    warnings: z.array(diagnosticSchema),
    limitations: z.array(diagnosticSchema).min(1),
    timestamps: z.object({
      verification_started_at: isoTimestampSchema,
      verification_completed_at: isoTimestampSchema,
    }),
    processing: z.object({
      mode: z.enum(['LOCAL_BROWSER', 'LOCAL_PROCESS', 'REMOTE_EXPLICIT']),
      duration_ms: z.number().nonnegative(),
      adapters: z.array(z.object({ name: z.string(), version: z.string() })),
      errors: z.array(z.object({ code: z.string(), adapter: z.string() })),
    }),
    privacy: z.object({
      processed_locally: z.boolean(),
      uploaded: z.boolean(),
      stored: z.boolean(),
      original_media_retained: z.boolean(),
    }),
    execution_disclosure: z.object({
      local_processing: z.boolean(),
      origntd_server_processing: z.boolean(),
      external_provider_processing: z.boolean(),
      media_transmitted: z.boolean(),
      destinations: z.array(z.object({
        name: z.string().min(1),
        kind: z.enum(['ORIGNTD_SERVER', 'EXTERNAL_SERVICE']),
        media_transmitted: z.boolean(),
        execution_state: z.enum(['COMPLETED', 'FAILED', 'TIMED_OUT']),
      }).strict()),
      retention: z.object({
        origntd_source_media: z.enum(['NOT_RETAINED', 'RETAINED']),
        external_provider_media: z.enum(['NOT_APPLICABLE', 'NOT_ASSERTED']),
      }).strict(),
    }).strict().optional(),
    capabilities: verificationCapabilitiesSchema.default(VERIFICATION_CAPABILITIES),
    raw_provider_evidence: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((result, context) => {
    const ids = new Set<string>([
      result.artifact.id,
      ...result.signals.map(({ id }) => id),
      ...result.credentials.map(({ id }) => id),
      ...result.claims.map(({ id }) => id),
      ...result.assertions.map(({ id }) => id),
      ...result.actions.map(({ id }) => id),
      ...result.actors.map(({ id }) => id),
      ...result.tools.map(({ id }) => id),
      ...result.warnings.map(({ id }) => id),
      ...result.limitations.map(({ id }) => id),
    ]);

    for (const conclusion of result.conclusions) {
      for (const reference of conclusion.trace_refs) {
        if (!ids.has(reference)) {
          context.addIssue({
            code: 'custom',
            message: `Conclusion ${conclusion.id} references unknown evidence ${reference}`,
            path: ['conclusions'],
          });
        }
      }
    }

    for (const edge of result.evidence_graph.edges) {
      if (!ids.has(edge.from) || !ids.has(edge.to)) {
        context.addIssue({
          code: 'custom',
          message: `Graph edge ${edge.id} has an unknown endpoint`,
          path: ['evidence_graph', 'edges'],
        });
      }
    }
  });

export type OverallStatus = z.infer<typeof overallStatusSchema>;
export type TrustStatus = z.infer<typeof trustStatusSchema>;
export type Validity = z.infer<typeof validitySchema>;
export type EvidenceMethod = z.infer<typeof evidenceMethodSchema>;
export type EvidenceStrength = z.infer<typeof evidenceStrengthSchema>;
export type Artifact = z.infer<typeof artifactSchema>;
export type Signal = z.infer<typeof signalSchema>;
export type Credential = z.infer<typeof credentialSchema>;
export type Claim = z.infer<typeof claimSchema>;
export type Assertion = z.infer<typeof assertionSchema>;
export type Action = z.infer<typeof actionSchema>;
export type Actor = z.infer<typeof actorSchema>;
export type Tool = z.infer<typeof toolSchema>;
export type Diagnostic = z.infer<typeof diagnosticSchema>;
export type Conclusion = z.infer<typeof conclusionSchema>;
export type TrustAssessment = z.infer<typeof trustAssessmentSchema>;
export type TrustResolution = z.infer<typeof trustResolutionSchema>;
export type TrustSnapshot = z.infer<typeof trustSnapshotSchema>;
export type IntegrityAssessment = z.infer<typeof integrityAssessmentSchema>;
export type EvidenceEdge = z.infer<typeof evidenceEdgeSchema>;
export type VerificationCapabilities = z.infer<typeof verificationCapabilitiesSchema>;
export type VerificationResult = z.infer<typeof verificationResultSchema>;
